import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { max } from 'lodash';
import { usePageVisibility } from 'react-page-visibility';
import {
  Exact,
  Maybe,
  SortOrder,
  TimeSignal,
  TimeSignalClassification,
  TimeSignalsCountDocument,
  TimeSignalsDocument,
  TimeSignalsInput,
  TimeSignalsQuery,
  TimeSignalsQueryVariables,
  useTimeSignalsLazyQuery
} from 'graphql/types';
import { cache } from 'graphql/client';
import { MIN_SUPPORTED_DATE } from 'utils/date-time';
import { useFeatureFlag } from 'modules/common/hooks';
import {
  shouldAddToCache,
  sortedTimeSignals
} from '../cache/utils/cache-utils';
import { useNotesSortContext } from '../NotesSortContextProvider';
import usePersistedCacheQuery from '../../common/hooks/usePersistedCacheQuery';
import { useTimeSignalGetParam } from './useTimeSignalParam';
import { useTimeSignalsCountQueryVariables } from './useTimeSignalsCount';

export type NotesTotalsByDay = Record<
  string,
  { count: number; totalDuration: number }
>;

export const transformedNotesInList = ({
  notes
}: {
  notes: TimeSignal[];
}): Record<string, TimeSignal[]> => {
  const dates = notes.map(({ date }) => date);
  const uniqueDates = Array.from(new Set(dates.length > 0 ? dates : [])).map(
    (date) => ({
      originalDate: date
    })
  );

  const noteMap = uniqueDates.reduce((acc, value) => {
    return {
      ...acc,
      [value.originalDate]: notes.filter(
        (note) => note.date === value.originalDate
      )
    };
  }, {} as Record<string, TimeSignal[]>);
  return noteMap;
};

export const sortedCachingSignals = (
  timeSignals: TimeSignal[],
  sortOrder: SortOrder,
  existingSignals: TimeSignalsQuery | null
): TimeSignal[] => {
  const notes = existingSignals?.timeSignals?.items || [];
  const notesMap = transformedNotesInList({ notes });
  let istimeSignalDateInList = true;
  const cachingSignalsObj = timeSignals.reduce((acc, item: TimeSignal) => {
    if (!(item.date in acc)) istimeSignalDateInList = false;
    acc[item.date] = sortedTimeSignals(acc[item.date] ?? [], sortOrder, item);
    return acc;
  }, notesMap);
  const sortedCachingSignalsObj = !istimeSignalDateInList
    ? Object.keys(cachingSignalsObj)
        .sort((a, b) => {
          const aDate = new Date(a);
          const bDate = new Date(b);
          return bDate.getTime() - aDate.getTime();
        })
        .reduce((obj: Record<string, TimeSignal[]>, key: string) => {
          obj[key] = cachingSignalsObj[key];
          return obj;
        }, {})
    : cachingSignalsObj;
  const sortedCachingSignals = Object.keys(sortedCachingSignalsObj).reduce(
    (acc: TimeSignal[], item: string) => {
      return [...acc, ...cachingSignalsObj[item]];
    },

    []
  );
  return sortedCachingSignals;
};

export const updateSignals = (
  prev: TimeSignalsQuery,
  {
    fetchMoreResult
  }: {
    fetchMoreResult?: TimeSignalsQuery | undefined;
    variables?:
      | Exact<{
          input?: Maybe<TimeSignalsInput> | undefined;
        }>
      | undefined;
  }
): TimeSignalsQuery => {
  if (!fetchMoreResult) return prev;
  const prevTimeSignalItems = prev?.timeSignals?.items ?? [];
  const timeSignalsMap = prevTimeSignalItems.reduce((acc, signal) => {
    acc[signal.id] = signal;
    return acc;
  }, {} as Record<string, TimeSignal>);

  const newSignals = fetchMoreResult.timeSignals.items.filter(
    (signal) => !timeSignalsMap || !timeSignalsMap[signal.id]
  );
  return {
    timeSignals: {
      totalCount: prev.timeSignals?.totalCount ?? 0,
      items: [...prevTimeSignalItems, ...newSignals],
      cursor: fetchMoreResult.timeSignals.cursor
    }
  };
};

export const useTimeSignals = (
  pageCount?: number
): {
  data: TimeSignalsQuery | undefined;
  loading: boolean;
  loadMore: (cursor: string) => void;
} => {
  const isTiwPauseTimesignalPollEnabled = useFeatureFlag(
    'isTiwPauseTimesignalPollEnabled'
  );
  const param = useTimeSignalGetParam(pageCount);
  const { data, loading, fetchMore } = usePersistedCacheQuery<
    TimeSignalsQuery,
    TimeSignalsQueryVariables
  >(TimeSignalsDocument, {
    variables: param,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  });

  const { sortOrder } = useNotesSortContext();

  const loadMore = useCallback(
    async (cursor: string) => {
      await fetchMore({
        variables: {
          input: {
            filter: param.input.filter,
            cursor: cursor,
            count: 10,
            sortFields: param.input.sortFields
          }
        },
        updateQuery: updateSignals
      });
    },
    [fetchMore, param.input.filter, param.input.sortFields]
  );

  const [modifiedTimeSignalsQuery, setModifiedTimeSignalsQuery] = useState<
    TimeSignalsQuery | undefined
  >();

  const autoSuggestReloadInterval = window.REPLICON_AUTO_RELOAD_INTERVAL || 60;

  const [getUpdatedSignals, { data: updatedData }] = useTimeSignalsLazyQuery({
    fetchPolicy: 'no-cache'
  });
  useEffect(() => {
    setModifiedTimeSignalsQuery(updatedData);
    return () => {
      setModifiedTimeSignalsQuery(undefined);
    };
  }, [updatedData]);
  const [modifiedTime, setModifiedTime] = useState<DateTime | undefined>(
    undefined
  );

  useEffect(() => {
    if (data?.timeSignals.items.length) {
      let maxModifiedTime = max(
        data!.timeSignals.items.map((x) => x.modificationTime)
      );
      maxModifiedTime = DateTime.fromISO(maxModifiedTime);
      setModifiedTime(maxModifiedTime.plus({ millisecond: 1 }));
    } else {
      setModifiedTime(DateTime.utc());
    }
  }, [data]);
  const isPageVisible = usePageVisibility();

  useEffect(() => {
    if (autoSuggestReloadInterval > 0) {
      const interval = setInterval(() => {
        const updatedParam = {
          input: {
            filter: {
              ...param.input.filter,
              ...{ modifiedFrom: modifiedTime?.toUTC() }
            },
            count: param.input.count,
            sortFields: param.input.sortFields
          }
        };
        if (!isTiwPauseTimesignalPollEnabled || isPageVisible) {
          getUpdatedSignals({ variables: updatedParam });
        }
      }, autoSuggestReloadInterval * 1000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [
    isTiwPauseTimesignalPollEnabled,
    autoSuggestReloadInterval,
    getUpdatedSignals,
    isPageVisible,
    modifiedTime,
    param
  ]);

  const countParam = useTimeSignalsCountQueryVariables(MIN_SUPPORTED_DATE);

  useEffect(() => {
    if (modifiedTimeSignalsQuery?.timeSignals.items.length) {
      const existingSignals: TimeSignalsQuery | null = cache.readQuery({
        query: TimeSignalsDocument,
        variables: param
      });
      if (existingSignals) {
        const newSignalsInRange = (
          modifiedTimeSignalsQuery!.timeSignals.items || []
        ).filter(
          (n) =>
            n.creationTime === n.modificationTime &&
            !existingSignals!.timeSignals.items.find(
              (x: TimeSignal) => x.id === n.id
            ) &&
            shouldAddToCache(existingSignals, n, sortOrder)
        );
        const newSignals = (
          modifiedTimeSignalsQuery!.timeSignals.items || []
        ).filter(
          (n) =>
            n.creationTime === n.modificationTime &&
            !existingSignals!.timeSignals.items.find(
              (x: TimeSignal) => x.id === n.id
            )
        );

        if (newSignalsInRange.length) {
          cache.writeQuery({
            query: TimeSignalsDocument,
            variables: param,
            data: {
              timeSignals: {
                __typename: 'TimeSignalsResult',
                items: sortedCachingSignals(
                  newSignalsInRange,
                  sortOrder,
                  existingSignals
                ),
                cursor: existingSignals!.timeSignals.cursor,
                totalCount:
                  existingSignals!.timeSignals.totalCount + newSignals.length
              }
            }
          });
          // updates timesignals counts for the tabs
          if (newSignals.length) {
            const existingTotals = cache.readQuery({
              query: TimeSignalsCountDocument,
              variables: countParam
            });
            const classificationMapping = {
              [TimeSignalClassification.Unclassified]: 'unClassified',
              [TimeSignalClassification.Private]: 'private',
              [TimeSignalClassification.Trash]: 'trash',
              [TimeSignalClassification.Accepted]: 'accepted'
            };
            const newTotals = newSignals.reduce((a, c) => {
              const classification = a[classificationMapping[c.classification]];
              if (classification) {
                const newCount = (classification.totalCount || 0) + 1;
                return {
                  ...a,
                  ...{
                    [classificationMapping[c.classification]]: {
                      ...classification,
                      ...{ totalCount: newCount }
                    }
                  }
                };
              }
            }, existingTotals as any);
            cache.writeQuery({
              query: TimeSignalsCountDocument,
              variables: countParam,
              data: newTotals
            });
          }
        }
        const updatedSignals =
          modifiedTimeSignalsQuery!.timeSignals.items.filter((signal) => {
            return (
              !newSignals.find((x) => x.id === signal.id) &&
              existingSignals!.timeSignals.items.find(
                (e: TimeSignal) => e.id === signal.id
              )
            );
          }) || [];
        if (updatedSignals.length) {
          setModifiedTimeSignalsQuery(undefined);

          let maxModifiedTime = max(
            updatedSignals.map((x) => x.modificationTime)
          );
          maxModifiedTime = DateTime.fromISO(maxModifiedTime);
          setModifiedTime(maxModifiedTime.plus({ millisecond: 1 }));
          const updatedExistingTimeSignal = existingSignals!.timeSignals.items.map(
            (item) => {
              const isPresent = updatedSignals.find(
                (x: any) => x.id === item.id
              );
              return isPresent ?? item;
            }
          );
          cache.writeQuery({
            query: TimeSignalsDocument,
            variables: param,
            data: {
              timeSignals: {
                __typename: 'TimeSignalsResult',
                items: [...updatedExistingTimeSignal],
                cursor: existingSignals!.timeSignals.cursor,
                totalCount:
                  existingSignals!.timeSignals.totalCount +
                  updatedSignals.length
              }
            }
          });
        }
      }
    }
  }, [param, modifiedTimeSignalsQuery, sortOrder, countParam]);

  return useMemo(
    () => ({
      data,
      loading,
      loadMore
    }),
    [data, loading, loadMore]
  );
};
